import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api/authApi';

const AuthContext = createContext(null);

const INITIAL_STATE = {
    isAuthenticated: false,
    isLoading: true,         // true while we check the session cookie on mount
    accessToken: null,
    refreshToken: null,
    user_type: null,
    // plan: "Free Trial",
    companyLogo: null,
    upload_id: null,
    interview_id: null,
    candidate_id: null,
    recruiter_id: null,
    interview_status: null,
    interview_mode: null,
    Interview_status: null,
    forget_pwd_email: null,
    otp_end_time: null,
    login_lock_until: {}
};

let wasLoggedOut = false;

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState(INITIAL_STATE);

    // ── On first mount, try to restore session from cookie ──────────────────
    useEffect(() => {
        const rehydrate = async () => {
            try {
                // Check if the user intentionally logged out in this browser
                const isLoggedOutCookie = document.cookie.split('; ').find(row => row.startsWith('logged_out='));
                if (isLoggedOutCookie && isLoggedOutCookie.split('=')[1] === 'true') {
                    setAuthState(prev => ({ ...prev, isAuthenticated: false, isLoading: false }));
                    return;
                }

                // authApi.profile() calls GET /user/profile/ with cookies,
                // handles decryption, and returns the profile data object directly.
                const profileResponse = await authApi.profile();
                const profile = profileResponse?.data || profileResponse;

                if (profile && profile.user_type) {
                    setAuthState(prev => ({
                        ...prev,
                        isAuthenticated: true,
                        user_type: profile.user_type,
                        // plan: profile.plan || prev.plan,
                        isLoading: false,
                    }));
                } else {
                    // Valid cookie but unexpected response shape — unauthenticated
                    setAuthState(prev => ({ ...prev, isAuthenticated: false, isLoading: false }));
                }
            } catch {
                // 401 / network error — no valid session
                setAuthState(prev => ({ ...prev, isAuthenticated: false, isLoading: false }));
            }
        };

        rehydrate();
    }, []);

    const setAuthData = (data) => {
        if (data.isAuthenticated) {
            // Clear the logged_out cookie on successful authentication
            document.cookie = "logged_out=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;";
            document.cookie = "logged_out=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=" + window.location.hostname;
            document.cookie = "logged_out=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.merubot.com";
        }
        setAuthState(prev => ({ ...prev, ...data }));
    };

    const logout = () => {
        // Set a client-side non-HttpOnly cookie indicating the user has logged out
        const expireDate = new Date();
        expireDate.setFullYear(expireDate.getFullYear() + 1); // 1 year expiry
        const cookieStr = "logged_out=true; expires=" + expireDate.toUTCString() + "; path=/;";
        document.cookie = cookieStr;
        document.cookie = cookieStr + " domain=" + window.location.hostname + ";";
        document.cookie = cookieStr + " domain=.merubot.com;";

        // Attempt to clear client-accessible cookies
        try {
            const cookies = document.cookie.split(";");
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i];
                const eqPos = cookie.indexOf("=");
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                if (name !== "logged_out") {
                    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
                    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
                    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.merubot.com";
                }
            }
        } catch (e) {
            // console.error("Cookie clearing error:", e);
        }

        setAuthState({ ...INITIAL_STATE, isLoading: false });
    };

    return (
        <AuthContext.Provider value={{ ...authState, setAuthData, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);


